<template>
  <ValidateWarning
    title="Missing Geometries"
    :show="show"
    :ignore="ignore"
    :btn-show-more="displayShowMoreBtn"
    @ignore="$emit('ignore')"
  >
    <template v-slot:warning-message>
      <p>
        {{ warningMessage }}
      </p>
    </template>
    <template v-slot:warning-list="slotProps">
      <ValidateWarningList
        v-if="csvFieldsNotInShp.length > 0 || unusedShpIds.length > 0"
        header="CSV geom IDs missing"
        :list="csvFieldsNotInShp"
        :show-more="slotProps.showMore"
      />
      <ValidateWarningList
        v-if="csvFieldsNotInShp.length > 0 || unusedShpIds.length > 0"
        header="Unused shapefile geom IDs"
        :list="unusedShpIds"
        :show-more="slotProps.showMore"
      />
    </template>
  </ValidateWarning>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import ValidateWarning from '@/components/admin/upload/ValidateWarning.vue'
import ValidateWarningList from '@/components/admin/upload/ValidateWarningList.vue'
import { useUploadStore } from '@/store/admin/upload'

const components = {
  ValidateWarning,
  ValidateWarningList,
}

const props = {
  v: {
    type: Object,
    required: true,
  },
  csvColumn: {
    type: String,
    required: true,
  },
  selected: {
    type: String,
    default: null,
  },
  ignore: {
    type: Boolean,
    default: false,
  },
}

// eslint-disable-next-line no-use-before-define
@Component<CsvGeomIDs>({
  components,
  watch: {
    show (show) {
      this.$emit('show', show)
    },
  },
})
export default class CsvGeomIDs extends Vue.extend({ props }) {
  upload = useUploadStore()
  get displayShowMoreBtn () {
    return this.csvFieldsNotInShp.length > 5 || this.unusedShpIds.length > 5
  }

  get show () {
    return (this.v.$dirty && (!this.v.csvGeomIds || !this.v.unusedGeomIds)) || this.ignore
  }

  get geom () {
    const geoms = this.upload.geom
    if ('features' in geoms) {
      return geoms
    } else {
      return { features: [] }
    }
  }

  get csvGeomIds () {
    return [...new Set(
      this.upload.rows.map(row => row[this.csvColumn]),
    )]
  }

  get shpGeomIds () {
    // Get all unique geom ids from the csv
    const shpGeomIds = new Set<string>()
    this.geom.features
      .forEach((feature) => {
        if (feature.properties) shpGeomIds.add(feature.properties[this.selected])
      })
    return Array.from(shpGeomIds)
  }

  get csvFieldsNotInShp () {
    // Find the csv geom ids that are not included in the shp geom ids
    const result = this.csvGeomIds.filter(id => !this.shpGeomIds.includes(id))
    // Check if no result is found
    this.$emit('valid', {
      key: 'csvGeoIdsAreInShp',
      value: result.length === 0,
    })
    return result
  }

  get unusedShpIds () {
    // Find the shp geom ids that are not included in the csv geom ids
    const result = this.shpGeomIds.filter(id => !this.csvGeomIds.includes(id))
    // Check if no result is found
    this.$emit('valid', {
      key: 'unusedGeoIdsAreInShp',
      value: result.length === 0,
    })
    return result
  }

  get warningMessage () {
    if (!this.v.csvGeomIds) {
      return 'There are geometry IDs in your CSV that are not present in the selected shapefile field. If you ignore this error, then the data associated with these geometries will not display on the map.'
    }
    if (!this.v.unusedGeomIds) {
      return 'There are geometry IDs with no associated data tied to them in your shapefile field. If you ignore this error, then these geometries will not display on the map.'
    }
    return null
  }
}

</script>
